import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { StringParam, useQueryParam } from 'use-query-params';

import { ReactQueryKeys } from '../../../common/react-query-keys';
import {
  getSessionCompanyId,
  getSessionToken,
} from '../../../selectors/session';
import { GlintsChatClient } from '../client/GlintsChatClient';
import { ChannelQueryParam } from '../constants';

/**
 * useResetChatOnCompanySwitch
 *
 * When the user switches the active company, the GlintsChat singleton and all
 * of its associated caches (WebSocket connection, React Query caches, global
 * channel list state, active channel URL params) are still bound to the
 * previous company. Without an explicit reset, the chat page and the sidebar
 * unread bubble show stale data for the old company until the user manually
 * refreshes the browser.
 *
 * This hook watches `sessionCompanyId` and, when it changes, performs a full
 * reset:
 *   1. Clears the chat-related URL params (`?cid`, `?pop_cid`, `?job_id`) first
 *      so that the chat page unmounts ChannelDetail before data is cleared
 *      (preventing assertIsDefined crashes), and the job filter resets
 *      since the previous company's job may not exist in the new company.
 *   2. Reconnects the WebSocket (the existing connection is server-side bound
 *      to the previous company and would never deliver new-company events).
 *   3. Invalidates the chat-related React Query caches so the next mount /
 *      render refetches from the API for the new company.
 *   4. Clears the global channel list cache.
 *
 * CompanySwitcher unmounts its Popover before committing the company change, so
 * this reset can run directly without depending on animation timing.
 */
export default function useResetChatOnCompanySwitch() {
  const sessionCompanyId = useSelector(getSessionCompanyId);
  const token = useSelector(getSessionToken);
  const queryClient = useQueryClient();
  const [, setActiveChannelId] = useQueryParam(
    ChannelQueryParam.CHANNEL_ID,
    StringParam
  );
  const [, setPopupChannelId] = useQueryParam(
    ChannelQueryParam.POPUP_CHANNEL_ID,
    StringParam
  );
  const [, setJobId] = useQueryParam(ChannelQueryParam.JOB_ID, StringParam);

  const previousCompanyIdRef = useRef<string | undefined>(sessionCompanyId);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    if (previousCompanyIdRef.current === sessionCompanyId) {
      return;
    }
    const previousCompanyId = previousCompanyIdRef.current;
    previousCompanyIdRef.current = sessionCompanyId;
    if (previousCompanyId === undefined) {
      return;
    }

    const client = GlintsChatClient.getActiveInstance();

    if (client) {
      if (client.isWSConnected()) {
        client.disconnect();
      }

      if (tokenRef.current) {
        client.connect().catch(() => {
          // best-effort; existing retry/reconnection flow handles failures
        });
      }
    }

    setActiveChannelId(undefined);
    setPopupChannelId(undefined);
    setJobId(undefined);

    queryClient.invalidateQueries({
      queryKey: ReactQueryKeys.globalChannelList(previousCompanyId),
    });
    queryClient.invalidateQueries({
      queryKey: ['glintsChatChannels', previousCompanyId],
    });
    queryClient.invalidateQueries({
      queryKey: ReactQueryKeys.messageListShort,
    });
    queryClient.invalidateQueries({
      queryKey: ReactQueryKeys.applicationDetailsShortKey,
    });
    queryClient.invalidateQueries({
      queryKey: ['channelsUnreadCount'],
    });
    queryClient.invalidateQueries({
      queryKey: ['glintsChatChannelList'],
    });

    queryClient.setQueryData(['glintsChatChannelList'], () => ({}));
  }, [
    sessionCompanyId,
    queryClient,
    setActiveChannelId,
    setPopupChannelId,
    setJobId,
  ]);
}
